import createModule from "../createModule"
import moment from "moment"
import view from "./view"
import { Creators as PortfolioRootCreators } from "modules/portfolio-root/model"
import { Creators as ProfileCreators } from "model/profileModal"
import { getFilteredData, getCategoryItemsSubset, getPerformanceAttribution } from "./selectors/data"
import { getSelectedDate, getEarliestDate, getLatestDate, getStartDate, getEndDate, getModelEarliestDate, getIsPeriodIntraday } from "modules/security-profile/selectors/dates"
import { computePositionSet, getSingleDateBase } from "modules/security-profile/helpers/security"
import { getMaxFactors } from "./selectors/performance"
import { getOptions } from "./selectors/options"
import { getDisplayType } from "./selectors/items"
import { getDetails } from "./selectors/details"
import { Creators } from "./model"
import { getIsRfrAttributionEnabled } from "selectors/features"
import { getResidualAttributionSummarySubquery } from "helpers/residualAttribution"

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    startDate: getStartDate(state),
    endDate: getEndDate(state),
    modelEarliestDate: getModelEarliestDate(state),
    isRfrAttributionEnabled: getIsRfrAttributionEnabled(state),
    isPeriodIntraday: getIsPeriodIntraday(state, props),
  }),
  data: [{
    resource: "securityPerformanceHistory",
    url: ({ modelId, security, base, startDate, endDate, modelEarliestDate, isRfrAttributionEnabled, isPeriodIntraday }) => {
      if (isPeriodIntraday) {
        if (!security) { return null }
        const { assetClass, ticker, mic, modelProviderId } = security
        base = getSingleDateBase(base, endDate)
        return {
          id: `securityPerformanceHistory-intraday-${modelId}${ticker}${mic}${endDate}${JSON.stringify(base)}${isRfrAttributionEnabled}`,
          query: `query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $endDate: Date!) {
          model (id: $modelId) {
            intradaySimulation(positionSet: $positionSet, base: $base, on: $endDate) {
              performance {
                date,
                totalReturn {
                  cumulativePercent
                }
                attribution {
                  summary {
                    factors {
                      cumulativePercent
                    }
                    specific {
                      cumulativePercent
                    }
                    trading {
                      cumulativePercent
                    }
                    ${isRfrAttributionEnabled ? getResidualAttributionSummarySubquery() : ""}
                  }
                  factors {
                    id
                    category
                    name
                    return {
                      cumulativePercent
                    }
                  }
                }
              }
            }
          }
        }`,
          variables: {
            modelId,
            positionSet: {
              dates: [{
                date: endDate,
                ...computePositionSet(assetClass, ticker, mic, modelProviderId),
              }],
            },
            base,
            endDate,
          },
        }
      }

      const { assetClass, ticker, mic, modelProviderId } = security
      // Start the position set in the past to make sure the date is available on the previous modeled date
      // We go one week in the past so we don't have to query the previous date
      let positionSetDate = moment(startDate).add(-1, "week").format("YYYY-MM-DD")
      // We never go earlier than the model start
      if (positionSetDate < modelEarliestDate) {
        positionSetDate = modelEarliestDate
      }

      base = getSingleDateBase(base, positionSetDate)

      return {
        id: `securityPerformanceHistory-${modelId}${ticker}${mic}${startDate}${endDate}${JSON.stringify(base)}${isRfrAttributionEnabled}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $startDate: Date!, $endDate: Date!) {
          model (id: $modelId) {
            simulation(positionSet: $positionSet, base: $base, from: $startDate, to: $endDate, interval: AUTO) {
              performance {
                date,
                totalReturn {
                  cumulativePercent
                }
                attribution {
                  summary {
                    factors {
                      cumulativePercent
                    }
                    specific {
                      cumulativePercent
                    }
                    trading {
                      cumulativePercent
                    }
                    ${isRfrAttributionEnabled ? getResidualAttributionSummarySubquery() : ""}
                  }
                  factors {
                    id
                    category
                    name
                    return {
                      cumulativePercent
                    }
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          positionSet: {
            dates: [{
              date: positionSetDate,
              ...computePositionSet(assetClass, ticker, mic, modelProviderId),
            }],
          },
          base,
          startDate,
          endDate,
        },
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    ...state.modules.securityProfilePerformance,
    earliestDate: getEarliestDate(state, props),
    latestDate: getLatestDate(state, props),
    date: getSelectedDate(state),
    data: getFilteredData(state),
    items: getCategoryItemsSubset(state),
    displayType: getDisplayType(state),
    options: getOptions(state),
    details: getDetails(state),
    performanceAttribution: getPerformanceAttribution(state, props),
    maxFactors: getMaxFactors(state, props),
  }),
  actions: {
    setDate: PortfolioRootCreators.setProfileDate,
    openFactorProfile: ProfileCreators.openFactorProfile,
    ...Creators,
  },
})
